import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, ArrowLeft, MessageSquare, Send } from "lucide-react";
import { Helmet } from "react-helmet-async";
import CircularProgress from "@mui/material/CircularProgress";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ name: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${SERVER_URL}/api/blogs/${id}`)
      .then((res) => res.json())
      .then((data) => setBlog(data))
      .catch((err) => console.error(err));

    fetch(`${SERVER_URL}/api/blogs/${id}/comments`)
      .then((res) => res.json())
      .then((data) => setComments(data))
      .catch((err) => console.error("Comments error:", err));
  }, [id]);

  const formatDateWithSuffix = (dateStr) => {
    const dateObj = new Date(dateStr);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getFullYear();
    const getSuffix = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    return `${day}${getSuffix(day)} ${month} ${year}`;
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.name || !newComment.text) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/blogs/${id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newComment),
      });
      const savedComment = await res.json();
      setComments((prev) => [savedComment, ...prev]);
      setNewComment({ name: "", text: "" });
    } catch (err) {
      console.error("Failed to submit comment", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!blog)
    return (
      <div className="min-h-screen flex flex-col justify-center items-center py-20 bg-gray-50">
        <CircularProgress style={{ color: "#eab308" }} size={50} thickness={4} />
        <p className="mt-6 text-gray-500 font-medium tracking-wide">Loading story...</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 mt-16 font-sans">
      <Helmet>
        <title>{blog.title} | Pak Fashions Blog</title>
        <meta
          name="description"
          content={
            blog.content.length > 160
              ? blog.content.slice(0, 160) + "..."
              : blog.content
          }
        />
        <meta
          name="keywords"
          content={`fashion, blog, article, ${blog.author}, ${blog.title}, Pak Fashions`}
        />
        <meta name="author" content={blog.author} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph (Facebook/LinkedIn) */}
        <meta property="og:title" content={blog.title} />
        <meta
          property="og:description"
          content={
            blog.content.length > 160
              ? blog.content.slice(0, 160) + "..."
              : blog.content
          }
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://pakfashions.co.ke/blog/${blog._id}`}
        />
        <meta
          property="og:image"
          content={
            blog.image || "https://pakfashions.co.ke/default-og-image.jpg"
          }
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta
          name="twitter:description"
          content={
            blog.content.length > 160
              ? blog.content.slice(0, 160) + "..."
              : blog.content
          }
        />
        <meta
          name="twitter:image"
          content={
            blog.image || "https://pakfashions.co.ke/default-og-image.jpg"
          }
        />

        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: blog.title,
            description:
              blog.content.length > 160
                ? blog.content.slice(0, 160) + "..."
                : blog.content,
            author: {
              "@type": "Person",
              name: blog.author,
            },
            datePublished: blog.date,
            image:
              blog.image || "https://pakfashions.co.ke/default-og-image.jpg",
            publisher: {
              "@type": "Organization",
              name: "Pak Fashions",
              logo: {
                "@type": "ImageObject",
                url: "https://pakfashions.co.ke/logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://pakfashions.co.ke/blog/${blog._id}`,
            },
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <div className="relative py-16 px-6 mb-12 flex flex-col justify-center items-center overflow-hidden bg-white border-b border-gray-100 shadow-sm rounded-b-[3rem]">
        <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-100 rounded-full blur-[100px] opacity-40 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-100 rounded-full blur-[100px] opacity-40 pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto w-full relative z-10 flex flex-col items-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm font-semibold text-gray-400 hover:text-yellow-600 transition-colors mb-8 self-start bg-gray-50 hover:bg-yellow-50 px-4 py-2 rounded-full border border-gray-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Style Journal
          </button>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-8 max-w-4xl mx-auto leading-[1.1] tracking-tight text-center text-gray-900">
            {blog.title}
          </h1>
          <div className="flex justify-center items-center gap-4 text-sm font-medium">
            <span className="flex items-center text-gray-500 bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
              <Calendar className="w-4 h-4 mr-2 text-yellow-500" />
              {formatDateWithSuffix(blog.date)}
            </span>
            <span className="flex items-center text-gray-500 bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
               By <span className="text-gray-900 font-bold ml-1">{blog.author}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <article className="max-w-3xl mx-auto px-6 py-6 pb-20">
        <div className="prose prose-lg prose-gray max-w-none text-gray-600 leading-relaxed font-medium">
          {blog.content.split("\n").map((para, idx) => (
            <p className="mb-6 first-letter:text-5xl first-letter:font-black first-letter:text-gray-900 first-letter:mr-1 first-letter:float-left" key={idx}>
              {para}
            </p>
          ))}
        </div>

        <div className="my-16 border-t border-gray-200 border-dashed w-3/4 mx-auto"></div>

        {/* Comment Section */}
        <section className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 flex items-center mb-8">
            <MessageSquare className="w-6 h-6 mr-3 text-yellow-500" /> Join the Discussion
          </h2>

          <form onSubmit={handleSubmitComment} className="mb-12 bg-gray-50 p-6 rounded-[2rem] border border-gray-100">
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Your name"
                className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all font-medium text-gray-900"
                value={newComment.name}
                onChange={(e) =>
                  setNewComment({ ...newComment, name: e.target.value })
                }
              />
              <textarea
                rows={3}
                placeholder="Share your thoughts..."
                className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all font-medium text-gray-900 resize-none"
                value={newComment.text}
                onChange={(e) =>
                  setNewComment({ ...newComment, text: e.target.value })
                }
              ></textarea>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-gray-900 text-white px-8 py-3.5 rounded-full font-bold hover:bg-black hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-70 disabled:hover:translate-y-0"
                >
                  {submitting ? "Posting..." : "Post Comment"}
                  {!submitting && <Send className="w-4 h-4 ml-1" />}
                </button>
              </div>
            </div>
          </form>

          {/* Display Comments */}
          <div className="space-y-6">
            {comments.length > 0 ? (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-4">
                  {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
                </h3>
                {comments.map((comment, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {comment.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="bg-gray-50 border border-gray-100 p-5 rounded-2xl rounded-tl-sm w-full">
                      <p className="text-base text-gray-900 font-bold mb-1">
                        {comment.name}
                      </p>
                      <p className="text-gray-600 font-medium leading-relaxed">{comment.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-400 font-medium italic">
                  Be the first to share your thoughts on this story.
                </p>
              </div>
            )}
          </div>
        </section>
      </article>
    </div>
  );
}

export default BlogPost;
